import { useState } from "react";
import { useParams } from "react-router";
import {
  Case,
  Switch,
  When,
} from "react-if";
import { Tooltip } from "@mui/material";
import {
  ActionType,
  MixpanelEvents,
  MixpanelLocationTypes,
  toDecimal,
} from "shared";
import { BanknotesIcon, FaceSmileIcon } from "@heroicons/react/24/outline";

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { useFetchBadInvoicesMetricsQuery } from "../../../services/api/organisationApi/organisation";
import { CardContent } from "../../../../common/Atoms/CardContent";
import { LoadingElementPlaceholder } from "../../../../common/Atoms/LoadingElementPlaceholder";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../../WrappedLink";
import { classNames } from "../../../../common/lib/classNames";
import { mixpanelService } from "../../../../common/lib/mixpanel";
import { useAdhocActionModal } from "../../../hooks/useAdhocActionModal";

// Days-overdue severity colors (design 2a)
function daysColor(days: number) {
  if (days >= 90) return `#c11415`;
  if (days >= 8) return `#b45309`;

  return `#6b7280`;
}

function renderCurrency(value: number, currencyCode: string) {
  const formatter = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: currencyCode,
  });

  return formatter.format(toDecimal(value));
}

export function BadInvoices() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const { openBulkReminders, openBulkLateFees } = useAdhocActionModal();
  const { data: response, isLoading } = useFetchBadInvoicesMetricsQuery({ orgId: organisationId }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const invoices = response?.invoices || [];

  // Count covers ALL overdue invoices, not just the 10 fetched rows
  const overdueInvoiceCount = response?.count || 0;
  const oldestDays = invoices.reduce((max, i) => Math.max(max, i.invoice.daysOverdue || 0), 0);
  // Filter through the current list so ids stale after a refetch are excluded
  const selectedInvoices = invoices.filter(i => selectedIds.includes(i.invoice.id));
  const selectedInvoiceIds = selectedInvoices.map(i => i.invoice.id);
  // Selected rows can span currencies, so sum per currency
  const selectedOverdueByCurrency = selectedInvoices.reduce((acc, i) => {
    acc[i.invoice.currency] = (acc[i.invoice.currency] || 0) + (i.invoice.totals.dueInCents || 0);

    return acc;
  }, {} as Record<string, number>);
  const selectedOverdueText = Object.entries(selectedOverdueByCurrency)
    .map(([currency, cents]) => renderCurrency(cents, currency))
    .join(` + `);

  function toggleSelected(invoiceId: string) {
    setSelectedIds(ids => ids.includes(invoiceId) ? ids.filter(id => id !== invoiceId) : [...ids, invoiceId]);
  }

  function onBulkReminders() {
    if (!selectedInvoiceIds.length) return;

    openBulkReminders(selectedInvoiceIds);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.BulkAdhocAction, {
      location: MixpanelLocationTypes.Dashboard,
      action_type: ActionType.REMINDER,
    });
  }

  function onBulkLateFees() {
    if (!selectedInvoiceIds.length) return;

    openBulkLateFees(selectedInvoiceIds);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.BulkAdhocAction, {
      location: MixpanelLocationTypes.Dashboard,
      action_type: ActionType.LATE_FEE,
    });
  }

  return (
    <Card className={ `w-full h-full flex flex-col overflow-hidden` }
      variant={ `soft` }>
      <MultiButtonCardHeader
        title={ `Oldest Invoices` }
        description={ `Invoices that are overdue by the most days` }
        variant={ `plain` }
      />
      <Switch>
        <Case condition={ isLoading }>
          <CardContent className={ `mt-4` }>
            <LoadingElementPlaceholder
              border={ false }
              rows={ 6 }
            />
          </CardContent>
        </Case>
        <Case condition={ !invoices.length }>
          <CardContent className={ `mt-4` }>
            <div className={ `min-h-[200px] h-full flex flex-col items-center justify-center text-center` }>
              <FaceSmileIcon className={ `w-12 h-12 mx-auto text-gray-300` } />
              <Paragraph variant={ `secondary` }>
                { `You have no overdue invoices!` }
              </Paragraph>
            </div>
          </CardContent>
        </Case>
        <Case condition={ invoices.length }>
          { /* Summary strip (design 2a) */ }
          <div className={ `mt-4 -mx-4 sm:-mx-5 grid grid-cols-2 border-y border-gray-100 bg-[#fafbfc] dark:bg-gray-50` }>
            <div className={ `py-3 px-4 sm:px-5 border-r border-gray-100` }>
              <div className={ `text-[11px] font-semibold tracking-wider text-gray-400 uppercase` }>{ `Invoices overdue` }</div>
              <div className={ `text-lg font-semibold mt-0.5` }>{ overdueInvoiceCount }</div>
            </div>
            <div className={ `py-3 px-4 sm:px-5` }>
              <div className={ `text-[11px] font-semibold tracking-wider text-gray-400 uppercase` }>{ `Oldest invoice` }</div>
              <div className={ `text-lg font-semibold mt-0.5` }>{ oldestDays }{ ` days` }</div>
            </div>
          </div>

          { /* Invoice rows */ }
          <div className={ `-mx-4 sm:-mx-5 grow` }>
            { invoices.map(invoice => {
              const selected = selectedIds.includes(invoice.invoice.id);

              return (
                <div
                  key={ invoice.invoice.id }
                  className={ classNames(
                    `grid grid-cols-[16px_110px_1fr_80px_100px] gap-x-3 items-center py-3 px-4 sm:px-5 border-b border-gray-100 cursor-pointer`,
                    selected ? `bg-[#f6f9f9] dark:bg-gray-200` : `hover:bg-gray-50`,
                  ) }
                  onClick={ () => toggleSelected(invoice.invoice.id) }
                >
                  <input
                    type={ `checkbox` }
                    checked={ selected }
                    onClick={ e => e.stopPropagation() }
                    onChange={ () => toggleSelected(invoice.invoice.id) }
                    className={ `h-4 w-4 rounded-sm border-gray-300 text-paidniceGreen-600 focus:ring-paidniceGreen-600 cursor-pointer` }
                  />
                  <div className={ `flex items-center space-x-1 min-w-0` }>
                    <Tooltip title={ invoice.invoice.number || `[No Number]` }>
                      <WrappedLink
                        to={ `/invoices/${invoice.invoice.id}` }
                        onClick={ e => e.stopPropagation() }
                        className={ `min-w-0` }
                      >
                        <Paragraph
                          variant={ `link` }
                          className={ `truncate font-semibold` }
                        >
                          { invoice.invoice.number || `[No Number]` }
                        </Paragraph>
                      </WrappedLink>
                    </Tooltip>
                    <When condition={ invoice.invoice.createdByUs }>
                      { /* Is a late fee invoice */ }
                      <Tooltip
                        title={ `This invoice was created as a late fee` }
                      >
                        <BanknotesIcon className={ `w-4 h-4 text-orange-400 shrink-0` } />
                      </Tooltip>
                    </When>
                  </div>
                  <WrappedLink
                    to={ `/contacts/${invoice.invoice.contactId}` }
                    onClick={ e => e.stopPropagation() }
                    className={ `min-w-0` }
                  >
                    <Paragraph
                      variant={ `secondary` }
                      className={ `truncate text-[13px]` }
                    >
                      { invoice.contact.name }
                    </Paragraph>
                  </WrappedLink>
                  <Tooltip title={ `Due On: ${invoice.invoice.dueDate}` }>
                    <span
                      className={ `text-[13px] font-semibold text-right` }
                      style={ { color: daysColor(invoice.invoice.daysOverdue || 0) } }
                    >
                      { invoice.invoice.daysOverdue }
                      { ` days` }
                    </span>
                  </Tooltip>
                  <Tooltip title={ `The due amount remaining out of ${renderCurrency(invoice.invoice.totals.totalInCents, invoice.invoice.currency)}` }>
                    <span className={ `text-sm font-semibold text-right tabular-nums` }>
                      { renderCurrency(invoice.invoice.totals.dueInCents, invoice.invoice.currency) }
                    </span>
                  </Tooltip>
                </div>
              );
            }) }
          </div>

          { /* Selection action bar */ }
          { selectedInvoiceIds.length > 0 && (
            <div className={ `-mx-4 sm:-mx-5 -mb-5 flex items-center justify-between flex-wrap gap-2 px-4 sm:px-5 py-3 bg-[#2f4e60]` }>
              <span className={ `text-[13px] text-slate-200 dark:text-[#e2e8f0]` }>
                <strong className={ `text-white` }>{ selectedInvoiceIds.length }{ ` selected` }</strong>
                { ` · ` }
                { selectedOverdueText }
                { ` overdue` }
              </span>
              <div className={ `flex gap-2.5` }>
                <button
                  type={ `button` }
                  className={ `rounded-lg border border-white/30 bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-[13px] font-medium text-white` }
                  onClick={ onBulkReminders }
                >
                  { `Email` }
                </button>
                <button
                  type={ `button` }
                  className={ `rounded-lg bg-[#e51d1e] hover:bg-[#c11415] px-3.5 py-1.5 text-[13px] font-semibold text-white` }
                  onClick={ onBulkLateFees }
                >
                  { `Issue late fees` }
                </button>
              </div>
            </div>
          ) }
        </Case>
      </Switch>
    </Card>
  );
}
